import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeStatusActive,
  createStatus,
  removeStatusScheme,
  separateStatus,
  setStatusEffort,
  setStatusLabel,
  setStatusSlaCategory,
  statusDetailsQuery,
  statusesListQuery,
  statusesShellQuery,
  updateStatus,
  type StatusFields,
  type StatusFlow,
  type StatusRenameConfirm,
  type StatusRow,
  type StatusesShell,
} from '../../api/settingsEaseedeskStatuses'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket status.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_status/index.php` + `list.php`, si gest cu gest fata de cele doua
 * fisiere din `public/js/views/easeedesk_settings/tickets/ticket_status/`.
 *
 * STATUSURILE SUNT RANDURI COMUNE
 * -------------------------------
 *
 * `it_easeedesk_ticket_status` n-are furnizor: un status e imprumutat de
 * fluxuri, si acelasi rand e citit de toate instantele. De-aia:
 *
 *  - cele marcate `default` nu se pot redenumi, si exista coloana "Shown as
 *    (this instance)" - numele sub care le vede furnizorul asta;
 *  - sub fiecare nume scrie CINE il mai foloseste, inainte de a apasa creionul;
 *  - la redenumire serverul raspunde cu o intrebare, si omul are trei drumuri:
 *    schimba pentru toti, renunta, sau ia un status NOU numai pentru fluxurile
 *    pe care le alege ("Separate").
 *
 * "Sters" inseamna scos din SCHEMA furnizorului; randul comun ramane, si de-aia
 * butonul trimite `scheme_id`, nu ID-ul statusului.
 */

const SLA_CATEGORIES = [
  { value: 1, label: 'Take over' },
  { value: 2, label: 'Identify' },
  { value: 3, label: 'Intervention' },
]

/** Numele local se salveaza cand omul iese din casuta, ca in `list-1.js`. */
export function LocalStatusLabel({ status, onSaved }: { status: StatusRow; onSaved(): void }) {
  const [value, setValue] = useState(status.label)
  const [saved, setSaved] = useState(status.label)

  useEffect(() => {
    setValue(status.label)
    setSaved(status.label)
  }, [status.label])

  const save = useMutation({
    mutationFn: (next: string) => setStatusLabel(status.ID, next),
    onSuccess: (_data, next) => {
      setSaved(next)
      toast(next ? `Shown as "${next}" here` : 'Back to the shared name', 'Statuses')
      onSaved()
    },
    onError: (error) => {
      setValue(saved)
      toast(
        error instanceof ApiError && error.message ? error.message : 'The name could not be saved.',
        'Statuses',
        'error',
      )
    },
  })

  return (
    <input
      type="text"
      className="form-control input-sm status-label"
      data-status={status.ID}
      value={value}
      placeholder={status.status_name}
      disabled={save.isPending}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value === saved) return
        save.mutate(value)
      }}
    />
  )
}

/** „used by 3: Incident, Change, …" de sub nume. */
function UsedBy({ types }: { types: string[] }) {
  if (types.length === 0) return null

  return (
    <div className="color-light-grey" style={{ fontSize: 11 }} title={types.join(', ')}>
      used by {types.length}: {types.slice(0, 3).join(', ')}
      {types.length > 3 ? '…' : ''}
    </div>
  )
}

/** Casuta de minute cu bifa ei, ca formularul din portal. */
function EffortCell({ status, onSaved }: { status: StatusRow; onSaved(): void }) {
  const [value, setValue] = useState(String(status.estimated_effort))

  useEffect(() => {
    setValue(String(status.estimated_effort))
  }, [status.estimated_effort])

  const save = useMutation({
    mutationFn: () => setStatusEffort(status.ID, Number(value) || 0),
    onSuccess: () => {
      toast('Setting changed', 'Success')
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  /* Numai starile „in lucru" (status_type = 1) au timp maxim de rezolvare. */
  if (status.status_type !== 1) return null

  return (
    <form
      className="status_estimated_effort"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <div className="input-group">
        <input
          className="form-control input-sm"
          type="text"
          name="estimated"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <span className="input-group-btn">
          <button className="btn btn-default btn-sm color-green2" type="submit" disabled={save.isPending}>
            <i className="fa fa-check"></i>
          </button>
        </span>
      </div>
    </form>
  )
}

function StatusRowCells({
  status,
  onReload,
  onEdit,
}: {
  status: StatusRow
  onReload(): void
  onEdit(status: StatusRow): void
}) {
  const own = status.default === 0

  const flip = useMutation({
    mutationFn: (on: boolean) => changeStatusActive(status.ID, on),
    onSuccess: () => {
      toast('Status changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const category = useMutation({
    mutationFn: (value: number) => setStatusSlaCategory(status.ID, value),
    onSuccess: () => {
      toast('SLA category changed', 'Success')
      onReload()
    },
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeStatusScheme(status.scheme_id),
    onSuccess: () => {
      toast('Ticket status deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr style={own ? undefined : { backgroundColor: '#EEE' }}>
      <td>
        <span className="label" style={{ color: '#FFF', backgroundColor: status.status_color }}>
          {status.status_name}
        </span>
      </td>
      <td>
        {status.status_name}
        <UsedBy types={status.used_by} />
      </td>
      <td>
        <LocalStatusLabel status={status} onSaved={onReload} />
      </td>
      <td>
        <label className={own ? 'color-blue' : 'color-grey'}>{own ? 'custom' : 'default'}</label>
      </td>
      <td className="text-center">
        <label className="label" style={{ color: '#FFF', backgroundColor: status.type_color }}>
          {status.status_type_name}
        </label>
      </td>
      <td className="text-center">
        <label className="label">
          {status.hidden === 0 ? (
            <i className="fa fa-eye color-success font-16"></i>
          ) : (
            <i className="fa fa-eye-slash color-danger font-16"></i>
          )}
        </label>
      </td>
      <td className="text-center">
        {status.record_sla === 1 ? (
          <i className="far fa-clock color-success font-16"></i>
        ) : (
          <i className="fa fa-ban color-danger font-16"></i>
        )}
      </td>
      <td className="text-center">
        {status.manager_permission === 1 ? (
          <i className="fas fa-circle color-success"></i>
        ) : (
          <i className="fas fa-circle color-danger"></i>
        )}
      </td>
      <td>
        <select
          className="form-control sla-category-select"
          data-status={status.ID}
          value={status.sla_category || ''}
          onChange={(e) => category.mutate(Number(e.target.value) || 0)}
        >
          <option value="">Select...</option>
          {SLA_CATEGORIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <EffortCell status={status} onSaved={onReload} />
      </td>
      <td style={{ textAlign: 'center' }}>
        {own && (
          <div className="btn-group" role="group" aria-label="controls">
            <button
              type="button"
              className="btn btn-danger btn-xs delete-status"
              data-status={status.scheme_id}
              onClick={() => {
                if (!window.confirm('Delete status ?\nYou will not be able to recover this status!')) return
                remove.mutate()
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="btn btn-primary btn-xs edit-status"
              data-status={status.ID}
              onClick={() => onEdit(status)}
            >
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="status_status"
          aria-label={`Activate ${status.status_name}`}
          title="Activate"
          checked={status.active === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
    </tr>
  )
}

export function TicketStatusPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(statusesShellQuery())
  const list = useQuery(statusesListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<StatusRow | null>(null)
  const [search, setSearch] = useState('')

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_status'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="Ticket statuses" />
  if (!shell.data) return <Spinner />

  const all = [...(list.data?.default_status_list ?? []), ...(list.data?.custom_status_list ?? [])]
  /* `searchStatus()` din macheta: cauta in numele statusului, fara diacritice. */
  const needle = search.trim().toLowerCase()
  const rows = needle === '' ? all : all.filter((row) => row.status_name.toLowerCase().includes(needle))

  return (
    <div>
      <h4>
        <strong>Ticket</strong> status
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit ticket <strong>statuses</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create status
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The status list" />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <>
            <input
              type="text"
              id="myInput"
              placeholder="Search for status.."
              title="Status"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '15%',
                fontSize: 15,
                padding: '5px 20px 5px 12px',
                border: '1px solid #ddd',
                marginBottom: 12,
              }}
            />
            <table className="table table-hover dataTable">
              <thead>
                <tr>
                  <th className="text-left">Display status</th>
                  <th className="text-left">Status name</th>
                  <th className="text-left" style={{ width: 180 }}>
                    Shown as (this instance)
                  </th>
                  <th className="text-left"></th>
                  <th className="text-center">Type</th>
                  <th className="text-center">Visible</th>
                  <th className="text-center">Record SLA</th>
                  <th className="text-center" style={{ width: 100 }}>
                    Manager permission
                  </th>
                  <th className="text-left" style={{ width: 200 }}>
                    SLA category
                  </th>
                  <th className="text-left" style={{ width: 120 }}>
                    Maximum completion time (minutes)
                  </th>
                  <th>Actions</th>
                  <th>Activate</th>
                </tr>
              </thead>
              <tbody id="list">
                {rows.map((status) => (
                  <StatusRowCells key={status.ID} status={status} onReload={reload} onEdit={setEdited} />
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <StatusModal open={creating} shell={shell.data} onClose={() => setCreating(false)} onSaved={reload} />
      <StatusModal
        status={edited ?? undefined}
        open={edited !== null}
        shell={shell.data}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

const EMPTY: StatusFields = {
  status_name: '',
  status_color: '#3c8dbc',
  status_type: 0,
  record_sla: false,
  hidden: false,
  manager_permission: false,
}

/**
 * `newStatusModal` si `editStatusModal`: acelasi formular.
 *
 * La EDITARE campurile vin de la server (`ticket_status_details`), nu din
 * atributele butonului: pe lista veche, un buton fara `data-` deschidea
 * fereastra GOALA, cu numele sters si culoarea pierduta, fara sa spuna nimic.
 *
 * Cand se schimba NUMELE unui status folosit, serverul raspunde cu o intrebare
 * in loc sa scrie - si atunci se arata cele trei drumuri.
 */
export function StatusModal({
  status,
  open,
  shell,
  onClose,
  onSaved,
}: {
  status?: StatusRow
  open: boolean
  shell: StatusesShell
  onClose(): void
  onSaved(): void
}) {
  const editing = status !== undefined
  const details = useQuery(statusDetailsQuery(open && editing ? status.ID : 0))
  const [fields, setFields] = useState<StatusFields>(EMPTY)
  const [confirm, setConfirm] = useState<{ old_name: string; new_name: string; used_by: string[]; flows: StatusFlow[] } | null>(
    null,
  )
  const [chosenFlows, setChosenFlows] = useState<number[]>([])

  useEffect(() => {
    if (!open) return
    setConfirm(null)
    setChosenFlows([])
    if (!editing) {
      setFields(EMPTY)
      return
    }
    if (!details.data) return
    setFields({
      status_name: details.data.status_name,
      status_color: details.data.status_color !== '' ? details.data.status_color : '#3c8dbc',
      status_type: details.data.status_type,
      record_sla: details.data.record_sla === 1,
      hidden: details.data.hidden === 1,
      manager_permission: details.data.manager_permission === 1,
    })
  }, [open, editing, details.data])

  const save = useMutation({
    /* Raspunsul lui `update` poate fi si intrebarea de redenumire, nu doar
       „gata": tipul e cel larg, ca sa incapa amandoua. */
    mutationFn: (confirmed: boolean): Promise<{ ok?: boolean } & Partial<StatusRenameConfirm>> =>
      editing ? updateStatus(status.ID, fields, confirmed) : createStatus(fields),
    onSuccess: (data) => {
      if (data.confirm === true) {
        setConfirm({
          old_name: data.old_name ?? '',
          new_name: data.new_name ?? '',
          used_by: data.used_by ?? [],
          flows: data.flows ?? [],
        })
        return
      }
      toast(editing ? 'Status successfully updated.' : 'New status successfully created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const separate = useMutation({
    mutationFn: () => separateStatus(status?.ID ?? 0, fields, chosenFlows),
    onSuccess: () => {
      toast('The status was separated for the chosen flows.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editStatusModal' : 'newStatusModal'}
      labelledBy={editing ? 'editStatusModalLabel' : 'newStatusModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit status' : 'New ticket status'}
    >
      {editing && !details.data ? (
        <Spinner />
      ) : confirm ? (
        <div>
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> <strong>{confirm.old_name}</strong> is a
            shared status. Renaming it to <strong>{confirm.new_name}</strong> changes the word for every flow that goes
            through it — {confirm.used_by.join(', ')}.
          </div>

          <p>You have three ways out:</p>

          <div className="form-group">
            <button type="button" className="btn btn-warning" onClick={() => save.mutate(true)} disabled={save.isPending}>
              Rename for everyone
            </button>{' '}
            <button type="button" className="btn btn-default" onClick={onClose}>
              Cancel
            </button>
          </div>

          <hr />

          <p>
            <strong>Or take a status of your own</strong> — a new status named <strong>{confirm.new_name}</strong>, with
            only the flows you pick moved onto it. The rest keep the old one, untouched.
          </p>

          <div className="form-group">
            {confirm.flows.map((flow) => (
              <div key={flow.workflow_id} className="checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={chosenFlows.includes(flow.workflow_id)}
                    onChange={(e) =>
                      setChosenFlows((ids) =>
                        e.target.checked
                          ? [...ids, flow.workflow_id]
                          : ids.filter((id) => id !== flow.workflow_id),
                      )
                    }
                  />{' '}
                  {flow.name} <span className="color-light-grey">({flow.types.join(', ')})</span>
                </label>
              </div>
            ))}
            {confirm.flows.length === 0 && (
              <div className="alert alert-info" style={{ margin: 0 }}>
                <i className="fas fa-info-circle" aria-hidden="true"></i> No flow of yours goes through this status.
              </div>
            )}
          </div>

          <div className="form-group aright">
            <button
              type="button"
              className="btn btn-primary"
              disabled={chosenFlows.length === 0 || separate.isPending}
              onClick={() => separate.mutate()}
            >
              Separate for {chosenFlows.length} flow(s)
            </button>
          </div>
        </div>
      ) : (
        <form
          id={editing ? 'edit-status-form' : 'new-status-form'}
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (fields.status_name.trim() === '') return
            save.mutate(false)
          }}
        >
          <div className="form-group">
            <label htmlFor="status-name" className="control-label">
              Status name:
            </label>
            <input
              type="text"
              className="form-control"
              id="status-name"
              name="status_name"
              value={fields.status_name}
              onChange={(e) => setFields((f) => ({ ...f, status_name: e.target.value }))}
            />
            {editing && details.data && details.data.used_by.length > 0 && (
              <span className="help-block">
                Used by {details.data.used_by.length}: {details.data.used_by.join(', ')}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="status-color" className="control-label">
              Status color:
            </label>
            <input
              type="color"
              className="form-control"
              id="status-color"
              name="status_color"
              value={fields.status_color}
              onChange={(e) => setFields((f) => ({ ...f, status_color: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status-type" className="control-label">
              Status type:
            </label>
            <select
              className="form-control"
              id="status-type"
              name="status_type"
              value={fields.status_type}
              onChange={(e) => setFields((f) => ({ ...f, status_type: Number(e.target.value) }))}
            >
              <option value={0}>Select..</option>
              {shell.status_types.map((type) => (
                <option key={type.ID} value={type.ID}>
                  {type.status_type_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="control-label">Record SLA</label>{' '}
            <input
              type="checkbox"
              name="record_sla"
              checked={fields.record_sla}
              onChange={(e) => setFields((f) => ({ ...f, record_sla: e.target.checked }))}
            />
          </div>

          <div className="form-group">
            <label className="control-label">Hidden</label>{' '}
            <input
              type="checkbox"
              name="hidden"
              checked={fields.hidden}
              onChange={(e) => setFields((f) => ({ ...f, hidden: e.target.checked }))}
            />
          </div>

          <div className="form-group">
            <label className="control-label">Manager permission</label>{' '}
            <input
              type="checkbox"
              name="manager_permission"
              checked={fields.manager_permission}
              onChange={(e) => setFields((f) => ({ ...f, manager_permission: e.target.checked }))}
            />
          </div>

          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button
              type="submit"
              className={editing ? 'btn btn-primary submit-update-status' : 'btn btn-primary submit-new-status'}
              disabled={fields.status_name.trim() === '' || save.isPending}
            >
              {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
